'use client';
import { Button } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import RadioGroup from '@mui/material/RadioGroup';
import React, { useState } from 'react';

import { PrintIcon, useTranslation } from '@abg/kit-base';

import { useAnalyticsGeneralEngagement } from '../../shared/analytics/hooks/use-analytics-general-engagement';
import { ActionDialog } from '../ActionDialog';

import { actionButtonStyle, radioGroupStyling } from './print.styling';
import RadioOptionWithDescription from './radio-options';
import { PrintReceiptProps } from './types';

const PrintReceipt = ({
  styleParts = {},
  printOption,
  onPrintOptionChange,
  i18n,
  printPageLink,
}: PrintReceiptProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const t = useTranslation(i18n);
  const { emitEvent: emitGeneralEngagementEvent } =
    useAnalyticsGeneralEngagement();

  const handleOpenModal = (event: React.MouseEvent<HTMLElement>) => {
    emitGeneralEngagementEvent(event.currentTarget);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handlePrint = () => {
    handleCloseModal();
    window.open(
      `${printPageLink}?tncrequired=${printOption === 'includeTerms'}`,
      '_blank',
    );
  };

  return (
    <>
      <Button
        variant="outlined"
        className="secondary"
        sx={actionButtonStyle}
        onClick={handleOpenModal}
        data-testid="reservation-details-printSummary-button"
      >
        {t('reservationConfirmation.bookingDetails.printCTA.label')}
        <PrintIcon />
      </Button>
      <ActionDialog
        isOpen={isModalOpen}
        title={t('reservationConfirmation.printReceipt.popup.title')}
        description={t(
          'reservationConfirmation.printReceipt.popup.description',
        )}
        onPrimaryAction={handlePrint}
        onClose={() => setIsModalOpen(false)}
        primaryActionLabel={t(
          'reservationConfirmation.printReceipt.popup.button.label',
        )}
        styleParts={styleParts}
        primaryActionDisabled={false}
      >
        <FormControl>
          <RadioGroup
            aria-labelledby="demo-controlled-radio-buttons-group"
            name="controlled-radio-buttons-group"
            value={printOption}
            onChange={(event) =>
              onPrintOptionChange((event.target as HTMLInputElement).value)
            }
            sx={radioGroupStyling}
          >
            <RadioOptionWithDescription
              value="excludeTerms"
              label={t(
                'reservationConfirmation.printReceipt.popup.withoutTermAndConditionRadioButton.label',
              )}
              description={t(
                'reservationConfirmation.printReceipt.popup.withoutTermAndCondition.description',
              )}
              printOption={printOption}
              styleParts={styleParts}
            />
            <RadioOptionWithDescription
              value="includeTerms"
              label={t(
                'reservationConfirmation.printReceipt.popup.termAndConditionRadioButton.label',
              )}
              description={t(
                'reservationConfirmation.printReceipt.popup.termAndCondition.description',
              )}
              printOption={printOption}
              styleParts={styleParts}
            />
          </RadioGroup>
        </FormControl>
      </ActionDialog>
    </>
  );
};

export default PrintReceipt;
